// The station modal (spec/13 §3.3, F-STN-16): one station, opened from its tile in a large dialog over the floor.
// It reads top-down in three layers, so it never becomes a wall of stacked forms:
//
//  1. A header that stays put: the station's number in its class colour, its name with the class muted, under it
//     its state and, for a PC that is off, when it was last seen; Previous and Next to step to the neighbouring PC
//     without closing (the arrow keys do the same when focus is not in a field or a tab list).
//  2. An action bar under it with the actions that apply now: Start session on a free PC, Sell time (paid) and Add
//     free time (a comp) in a session, Lock, Wake on a PC that is off, Message… (a small centred dialog, to the session
//     while one runs so it is on its timeline), Maintenance, Skip setup on a PC that is setting up, and a "More" menu
//     for power, the offline code and the recovery code. Each keeps its own permission (station-actions.tsx); the
//     short forms open inline, under the bar, never a modal on a modal (Message is the one dialog). An offline PC
//     adds one slim line saying what commands do.
//  3. Pill tabs for the detail, scrolling on their own: Overview (what is on the PC now, the session and its clock,
//     its facts), Games and apps (what is installed, what needs attention, Install and Retry) and Health (health, wake
//     readiness, the client, the lockdown test). The tab it opens on follows the station: Health when it needs
//     attention, else Overview, and the operator's own last pick for that kind of station is remembered in this browser.
//     A PC that starts from the venue's boot server (Clavian Boot, ADR-0065) adds **Windows & games** — its image and
//     game disks, its last start, and Restart onto the latest when free, Reset this PC's changes and Change Windows using
//     this PC (../boot/StationBoot.tsx) — for a member holding `boot:view`.
//
// A station **run by staff** (ADR-0033: a console, a sim, a table with no app) keeps the same shape with what makes
// sense for it: Start session, Sell time, Lock (on the server: there is no kiosk) and "Countdown screen"
// for a TV or tablet beside it; no Message, Maintenance, power or codes, no Health tab, and its facts say who keeps
// the time. Since ADR-0034 it is **booked**: Start session books controllers and a block (or Open) with the price
// previewed, and a booked session's bar offers Sell time (one more block, added to what is due) and Controllers, and
// no free time: it is paid once, at the counter, when it ends.
//
// On a narrow screen the dialog is the whole screen. For a one-click action staff need not open it at all: the
// tile's quick-actions menu (quick-actions.tsx) offers the common ones with the same gates and confirms.

import type { BootFloorPc, FloorStationState, Session, StationClass, Zone } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, cx, Dialog, Menu, StatusPill, Tabs, VisuallyHidden, type MenuEntry, type TabItem } from '@clavian/ui-web';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import type { Access } from '../api/queries';
import { bootStripOf } from '../boot/floor-boot';
import { StationBoot } from '../boot/StationBoot';
import { CompForm, SellForm, StartSessionForm, StationSessionPanel } from '../org/sessions/StationSessionPanel';
import { ConsoleAddTimeForm, ConsoleControllersForm, ConsoleStartForm } from '../org/sessions/ConsoleSession';
import { useStationSession } from '../org/sessions/queries';
import { useCurrentVenue } from '../shell/scope';
import { Icon } from '../shell/nav-icons';
import { bootLabel } from '../stations/client';
import { useStationClients } from '../stations/queries';
import { Facts, Notice, Section } from '../ui';
import { CountdownScreenPanel } from './CountdownScreen';
import { clockOf, hasNoClient, isStaffRun, timeOfDay, useNow, type FloorState, type FloorStationView } from './live';
import { NowPlaying, stationNowPlaying } from './now-playing';
import { useStationWakeStatus } from './queries';
import {
  MaintenanceControls,
  MessageControls,
  OfflineCodeControls,
  PowerConfirm,
  runningSession,
  SkipSetupConfirm,
  stationActionGates,
  useMaintenance,
  usePowerNow,
  useSessionLock,
  type StationActionGates,
} from './station-actions';
import { StationRemovalDialog, type RemovalTarget } from '../stations/StationRemoval';
import { StationApps } from './StationApps';
import { StationHealth } from './StationHealth';
import { STATIONS_ALERTS_CLEAR } from './clear-alerts';
import { StationClientSection } from './StationClient';
import { LockdownTestSection, RecoveryCodeSection } from './StationLockdown';
import { pillOf } from './tile';
import { WakeReadiness } from './WakeReadiness';

const SESSIONS_READ = 'sessions:read';
const SESSIONS_START = 'sessions:start';
const SESSIONS_COMP = 'sessions:comp';
const LIBRARY_READ = 'library:read';
const LIBRARY_MANAGE = 'library:manage';
const STATIONS_MANAGE = 'stations:manage';
const STATIONS_MAINTENANCE = 'stations:maintenance';
const STATIONS_READ = 'stations:read';

/** The states in which the modal reads the station's session (the Session tab), as the sheet always did. */
const SESSION_STATES: ReadonlySet<FloorState> = new Set(['session', 'ending', 'overdue', 'locked', 'free']);
/** The states in which someone is on the station. */
const LIVE_STATES: ReadonlySet<FloorState> = new Set(['session', 'ending', 'overdue', 'locked']);

export type StationTab = 'overview' | 'apps' | 'health' | 'boot';
/** What the station is doing, for choosing the tab it opens on. */
export type StationTabContext = 'session' | 'attention' | 'idle';
/** An action opened from the bar: its form or confirm, inline under the bar. */
type BarAction =
  | 'start'
  | 'sell'
  | 'comp'
  | 'maintenance'
  | 'restart'
  | 'shutdown'
  | 'recovery'
  | 'offlineCode'
  | 'screen'
  | 'addTime'
  | 'controllers'
  | 'skipSetup';

const TAB_ORDER: readonly StationTab[] = ['overview', 'apps', 'boot', 'health'];
const DEFAULT_TAB: Readonly<Record<StationTabContext, StationTab>> = { session: 'overview', attention: 'health', idle: 'overview' };
/** Where the operator's last tab pick per kind of station is kept, in this browser. */
export const STATION_TAB_KEY = 'clavian.floor.stationTab';

export type RememberedTabs = Partial<Record<StationTabContext, StationTab>>;

/** The operator's last pick for each kind of station. Empty when storage is unavailable or unreadable. */
function readRememberedTabs(): RememberedTabs {
  try {
    const raw = window.localStorage.getItem(STATION_TAB_KEY);
    if (raw === null) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: { -readonly [K in StationTabContext]?: StationTab } = {};
    for (const context of ['session', 'attention', 'idle'] as const) {
      const value = (parsed as Record<string, unknown>)[context];
      // The Session and PC tabs of before are Overview now.
      if (value === 'session' || value === 'pc') out[context] = 'overview';
      else if (value === 'overview' || value === 'apps' || value === 'health' || value === 'boot') out[context] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function rememberTabs(remembered: RememberedTabs): void {
  try {
    window.localStorage.setItem(STATION_TAB_KEY, JSON.stringify(remembered));
  } catch {
    // A private window: the pick lasts until the page closes.
  }
}

/**
 * The tab a station opens on: the operator's pick this time, else their last pick for this kind of station,
 * else Health when it needs attention, Overview otherwise — the first of those they may see.
 */
export function chooseStationTab(
  visible: readonly StationTab[],
  context: StationTabContext,
  picked: StationTab | null,
  remembered: RememberedTabs,
): StationTab {
  const order = [picked, remembered[context], DEFAULT_TAB[context], 'overview' as const];
  return order.find((tab): tab is StationTab => tab !== null && tab !== undefined && visible.includes(tab)) ?? visible[0] ?? 'overview';
}

/** Arrow keys step between stations, except where they already mean something: fields, tab lists, radios, menus. */
const OWNS_ARROWS = 'input, textarea, select, [contenteditable="true"], [role="tab"], [role="tablist"], [role="radio"], [role="radiogroup"], [role="menu"], [role="menuitem"], [role="slider"], [role="listbox"], [role="option"]';

export function StationModal({
  view,
  klass,
  zone,
  access,
  boot,
  onClose,
  onStep,
  onCloseAutoFocus,
}: {
  readonly view: FloorStationView | null;
  readonly klass: StationClass | undefined;
  readonly zone: Zone | undefined;
  readonly access: Access;
  /** How the PC starts from the boot server (Clavian Boot), when it does and the member may see it. */
  readonly boot?: BootFloorPc | undefined;
  readonly onClose: () => void;
  /** Step to the previous (-1) or next (1) station on the floor, keeping the modal open. */
  readonly onStep?: (direction: -1 | 1) => void;
  /** Where focus returns on close: the tile of the station it ended on. */
  readonly onCloseAutoFocus?: (event: Event) => void;
}): ReactNode {
  const station = view?.station;

  return (
    <Dialog
      open={view !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={station === undefined ? '' : <StationTitle station={station} klass={klass} />}
      description={view === null ? undefined : <StationStatusLine view={view} />}
      // The tile's shape in the header (ADR-0043): the state's rail along the dialog's top edge and its wash fading down
      // behind the header — ember for a session, warn ending, danger overdue — and a hatch for a PC that cannot play.
      className={cx('console-station-modal', view !== null && `console-station-modal--${view.state}`)}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {view === null ? null : <StationModalBody view={view} zone={zone} className={klass?.name} access={access} bootPc={boot} onStep={onStep} onClose={onClose} />}
    </Dialog>
  );
}

/**
 * The dialog's title: the station's number in its class colour (decoration; the name carries it), then its name with the
 * class muted. The accessible name is the station's name alone.
 */
function StationTitle({ station, klass }: { readonly station: FloorStationState; readonly klass: StationClass | undefined }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <>
      <span
        className="console-station-sheet__number console-figure console-station-modal__number"
        aria-hidden="true"
        style={klass === undefined ? undefined : ({ '--tile-class': `var(--clavian-${klass.colorToken})` } as CSSProperties)}
      >
        {station.number}
      </span>
      {station.name ?? t('floor.tile.numbered', { number: station.number })}
      {klass === undefined ? null : (
        <span className="console-muted console-station-modal__class" aria-hidden="true">
          {` · ${klass.name}`}
        </span>
      )}
    </>
  );
}

/**
 * Under the name: the state as a pill, and for a PC that is off when it was last seen ("Last seen 2 hours ago"; "Never seen"
 * only for one that never reported). The clock says the time, so a session's pill names the state rather than repeating it.
 */
function StationStatusLine({ view }: { readonly view: FloorStationView }): ReactNode {
  const { t } = useTranslation('console');
  const formatter = useVenueFormatter();
  const now = useNow(30_000);
  const station = view.station;
  const pill = pillOf(view, t);
  const words = view.state === 'session' ? t('floor.state.session') : view.state === 'ending' ? t('floor.state.ending') : pill.words;
  const lastSeen =
    isStaffRun(station) || hasNoClient(station) || station.online
      ? null
      : station.lastSeenAt === null
        ? t('floor.drawer.lastSeenNever')
        : t('floor.drawer.lastSeenAgo', { when: formatter.relative(station.lastSeenAt, now) });
  return (
    <span className="console-station-modal__status">
      <StatusPill state={pill.state} pulse={pill.state === 'session'}>
        {words}
      </StatusPill>
      {station.status === 'disabled' && station.disabledReason !== null ? <span className="console-muted">{station.disabledReason}</span> : null}
      {lastSeen === null ? null : <span className="console-muted">{lastSeen}</span>}
    </span>
  );
}

function StationModalBody({
  view,
  zone,
  className,
  access,
  bootPc,
  onStep,
  onClose,
}: {
  readonly view: FloorStationView;
  readonly zone: Zone | undefined;
  /** The station's class, which the Windows & games tab names when the PC's Windows comes from it. */
  readonly className: string | undefined;
  readonly access: Access;
  readonly bootPc: BootFloorPc | undefined;
  readonly onStep: ((direction: -1 | 1) => void) | undefined;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const station = view.station;
  const ready = access.status === 'ready';
  const canSessions = ready && access.can(SESSIONS_READ);
  const canLibrary = ready && access.can(LIBRARY_READ);
  const canRescan = ready && access.can(STATIONS_MANAGE);
  // Install one app on this PC (ADR-0056): `library:manage`, the Install for players switch's own permission.
  const canInstallApps = ready && access.can(LIBRARY_MANAGE);
  const canStations = ready && access.can(STATIONS_READ);
  // F-STN-11 (2026-10-02): clear or snooze a health warning by hand.
  const canClearAlerts = ready && access.can(STATIONS_ALERTS_CLEAR);
  // F-STN-12 Update now (2026-10-07): the people who keep the PCs — `stations:maintenance`.
  const canUpdateClient = ready && access.can(STATIONS_MAINTENANCE);
  // What it is running now (F-CAT-04), counted to the minute while the modal is open.
  const now = useNow(30_000);
  const staffRun = isStaffRun(station);
  // A PC with no Clavian Station app (ADR-0065): nothing to start, sell, lock or message, no health or games to report —
  // its window is its facts and how it starts (Overview, Windows & games).
  const noClient = hasNoClient(station);

  // The session, read where the Session tab reads it (the same query and cache entry, so no second request):
  // the bar's Sell time, Add free time, Message and Lock act on it.
  const readsSession = canSessions && SESSION_STATES.has(view.state);
  const sessionRead = useStationSession(venue.organizationId, venue.id, station.stationId, readsSession);
  const running = readsSession ? runningSession(sessionRead.data) : null;
  const gates = stationActionGates(access, running, station);
  // What it runs now: the title the app detected, or — on a staff-run station — the title staff picked (ADR-0033).
  const playing = staffRun
    ? running?.staffTitle === undefined
      ? null
      : t('floor.drawer.staffRun.title', { title: running.staffTitle.name })
    : stationNowPlaying(station, now, t);
  // The countdown screen (ADR-0033): for a staff-run station, to a role that may manage stations.
  const canScreen = staffRun && canRescan;
  // A free station (its session read, and none running): Start session leads the bar (F-SES-04), as the Session tab
  // offered it before — `sessions:start`, with comp funding for a role that may comp.
  const canStart = readsSession && sessionRead.data === null && ready && access.can(SESSIONS_START);
  const canComp = ready && access.can(SESSIONS_COMP);

  // How the PC starts (2026-10-07, spec/10 §14): "Diskless (CCBoot)" on the PC tab, from the venue's client list (the same
  // read the Health tab's Station client section makes, so no second request).
  const clients = useStationClients(venue.organizationId, venue.id, canStations && !staffRun && !noClient);
  const clientRow = clients.data?.data.find((row) => row.stationId === station.stationId);
  const boot = clientRow?.boot ?? null;

  // Needs attention: offline, an open health alert, or Wake-on-LAN not ready (the Health tab's own reads).
  const wake = useStationWakeStatus(venue.organizationId, venue.id, station.stationId, canStations && !staffRun && !noClient);
  const openAlerts = station.health?.openAlerts ?? 0;
  const needsAttention = !staffRun && !noClient && (view.state === 'offline' || openAlerts > 0 || wake.data?.readiness?.ready === false);
  const context: StationTabContext = LIVE_STATES.has(view.state) ? 'session' : needsAttention ? 'attention' : 'idle';

  // A tab is shown only when its permission leaves something in it; Overview always has the station's facts. A staff-run
  // station has no app to report its health or what is installed (ADR-0033): no Health and no Games and apps tab.
  // Windows & games: a PC that starts from the boot server, read with `boot:view` (useFloorBoot reads nothing without it).
  const visible = TAB_ORDER.filter((tab) =>
    tab === 'apps' ? canLibrary && !staffRun && !noClient : tab === 'health' ? canStations && !staffRun && !noClient : tab === 'boot' ? bootPc !== undefined && !staffRun : true,
  );
  const [remembered, setRemembered] = useState(readRememberedTabs);
  const [picked, setPicked] = useState<StationTab | null>(null);
  const tab = chooseStationTab(visible, context, picked, remembered);
  const scroller = useRef<HTMLDivElement>(null);
  const pick = (value: string) => {
    const next = value as StationTab;
    setPicked(next);
    const updated = { ...remembered, [context]: next };
    setRemembered(updated);
    rememberTabs(updated);
    scroller.current?.scrollTo?.({ top: 0 });
  };

  const [action, setAction] = useState<BarAction | null>(null);
  // Message… is the one action that opens a small centred dialog, not a form under the bar.
  const [messaging, setMessaging] = useState(false);
  // Unpair… and Remove station… (ADR-0057) confirm in their own small centred dialog.
  const [removal, setRemoval] = useState<RemovalTarget['kind'] | null>(null);
  // A PC is paired when a device is bound to it, online or not (`paired`). An older server's floor does not say: then a PC
  // that has reported in and is not waiting at its pairing screen.
  const paired = !staffRun && !noClient && (station.paired ?? (station.mode !== 'pairing' && (station.mode !== null || station.lastSeenAt !== null)));
  // Stepping to another station starts it fresh — its own default tab, no form open — while the header and its
  // Previous and Next stay mounted, so focus stays on the control that stepped.
  const [shownStation, setShownStation] = useState(station.stationId);
  if (shownStation !== station.stationId) {
    setShownStation(station.stationId);
    setPicked(null);
    setAction(null);
    setMessaging(false);
    setRemoval(null);
  }
  const toggle = (next: BarAction) => setAction((current) => (current === next ? null : next));
  const close = () => setAction(null);

  // Left and right step to the neighbouring station while focus is anywhere in this dialog but a control that
  // uses the arrows itself. The dialog's close button sits outside this body, so it listens on the document.
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (onStep === undefined) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const target = event.target instanceof Element ? event.target : null;
      const dialog = root.current?.closest('[role="dialog"]');
      if (target === null || dialog === null || dialog === undefined || !dialog.contains(target) || target.closest(OWNS_ARROWS) !== null) return;
      event.preventDefault();
      onStep(event.key === 'ArrowLeft' ? -1 : 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onStep]);

  const timed = LIVE_STATES.has(view.state) && view.secondsLeft !== null;
  const openPlayed = running?.booking?.open === true ? Math.max(0, (view.now - new Date(running.startedAt).getTime()) / 1000) : null;
  const content: Record<StationTab, ReactNode> = {
    overview: (
      <div key={station.stationId} className="console-station-modal__stack">
        {timed || openPlayed !== null || playing !== null ? <StationNow view={view} session={running} playing={playing} openPlayed={openPlayed} /> : null}
        {readsSession ? (
          <StationSessionPanel key={station.stationId} organizationId={venue.organizationId} venueId={venue.id} stationId={station.stationId} can={access.can} freeHint={t('floor.drawer.freeHint')} />
        ) : null}
        <Section title={t('floor.drawer.station')} titleAs="h3">
          <Facts
            columns={2}
            items={[
              { key: 'kind', label: t('floor.drawer.kind'), value: t(`stations.kind.${station.kind}`) },
              ...(zone === undefined ? [] : [{ key: 'zone', label: t('floor.drawer.zone'), value: zone.name }]),
              // Seats mean something only where a station has them to give (a console bay's controllers), not for a PC.
              ...(station.kind === 'pc' ? [] : [{ key: 'capacity', label: t('floor.drawer.capacity'), value: <span className="console-num">{station.capacity}</span> }]),
              ...(noClient
                ? [{ key: 'start', label: t('boot.floor.section'), value: bootPc === undefined ? t('boot.bootOnly.tile.noPc') : bootStripOf(bootPc, t).words }]
                : staffRun
                ? [{ key: 'runs', label: t('floor.drawer.staffRun.fact'), value: t('floor.drawer.staffRun.value') }]
                : [
                    { key: 'mode', label: t('floor.drawer.mode'), value: station.mode === null ? t('floor.drawer.noMode') : t(`floor.stationMode.${station.mode}`) },
                    ...(boot === null ? [] : [{ key: 'boot', label: t('stations.client.boot.label'), value: bootLabel(boot, t) }]),
                    {
                      key: 'seen',
                      label: t('floor.drawer.lastSeen'),
                      value: station.lastSeenAt === null ? t('floor.drawer.neverSeen') : <span className="console-num">{new Date(station.lastSeenAt).toLocaleString()}</span>,
                    },
                    ...(clientRow?.clientVersion == null ? [] : [{ key: 'client', label: t('floor.drawer.clientVersion'), value: <span className="console-num">{clientRow.clientVersion}</span> }]),
                  ]),
            ]}
          />
          {staffRun ? <p className="console-muted">{t('floor.drawer.staffRun.hint')}</p> : null}
          {noClient && access.products.stations ? <p className="console-muted">{t('boot.bootOnly.tile.waitingForApp')}</p> : null}
        </Section>
      </div>
    ),
    apps: canLibrary && !staffRun && !noClient ? <StationApps key={station.stationId} station={station} online={station.online} canRescan={canRescan} canInstall={canInstallApps} now={now} /> : null,
    boot:
      bootPc === undefined ? null : (
        <StationBoot
          key={station.stationId}
          scope={{ organizationId: venue.organizationId, venueId: venue.id }}
          pc={bootPc}
          className={className}
          rights={{ canManage: ready && access.can('boot:manage'), canWipe: ready && access.can('boot:wipe'), canEditImage: ready && access.can('boot:edit_image') }}
          busy={LIVE_STATES.has(view.state)}
        />
      ),
    health: canStations && !noClient ? (
      <div key={station.stationId} className="console-station-modal__grid">
        <StationHealth station={station} canClear={canClearAlerts} />
        <WakeReadiness station={station} />
        <StationClientSection station={station} canUpdate={canUpdateClient} />
        <LockdownTestSection stationId={station.stationId} canRun={canRescan} />
      </div>
    ) : null,
  };

  const tabLabel = (value: StationTab): ReactNode =>
    value === 'health' && openAlerts > 0 ? (
      <>
        {t('floor.drawer.tabs.health')}
        <span className="console-station-modal__tab-count" aria-hidden="true">
          {openAlerts}
        </span>
        <VisuallyHidden>{`, ${t('floor.drawer.tabs.alerts', { count: openAlerts })}`}</VisuallyHidden>
      </>
    ) : (
      t(`floor.drawer.tabs.${value}`)
    );
  const items: TabItem[] = visible.map((value) => ({ value, label: tabLabel(value), content: content[value] }));

  return (
    <div ref={root} className="console-station-modal__body">
      {onStep === undefined ? null : (
        <div className="console-station-modal__step" role="group" aria-label={t('floor.drawer.header.step')}>
          <Button variant="ghost" size="sm" icon={<Icon name="prev" />} label={t('floor.drawer.header.previous')} onClick={() => onStep(-1)} />
          <Button variant="ghost" size="sm" icon={<Icon name="next" />} label={t('floor.drawer.header.next')} onClick={() => onStep(1)} />
        </div>
      )}
      <div className="console-station-modal__top">
        <StationActionBar
          station={station}
          session={running}
          locked={view.state === 'locked' || running?.status === 'locked'}
          open={action}
          onToggle={toggle}
          onMessage={() => setMessaging(true)}
          onRemoval={canRescan ? setRemoval : undefined}
          paired={paired}
          can={gates}
          canStart={canStart}
          canScreen={canScreen}
          canWake={!station.online && view.state === 'offline' && gates.power && (wake.data === undefined || wake.data.macAddresses.length > 0)}
          settingUp={view.state === 'settingUp'}
        />
        {view.state === 'offline' && !staffRun ? (
          <Notice tone="info" className="console-station-modal__notice">
            {t('floor.drawer.offlineHint')}
          </Notice>
        ) : null}
      </div>

      <div ref={scroller} className="console-station-modal__scroll">
        {action === null ? null : (
          <ActionPanel key={action}>
            {action === 'start' && canStart ? (
              staffRun ? (
                <ConsoleStartForm organizationId={venue.organizationId} venueId={venue.id} station={station} pickGame={canLibrary} onDone={close} />
              ) : (
                <StartSessionForm organizationId={venue.organizationId} venueId={venue.id} stationId={station.stationId} canComp={canComp} onDone={close} />
              )
            ) : null}
            {action === 'addTime' && running !== null ? <ConsoleAddTimeForm organizationId={venue.organizationId} session={running} onDone={close} /> : null}
            {action === 'controllers' && running !== null ? (
              <ConsoleControllersForm organizationId={venue.organizationId} session={running} capacity={station.capacity} onDone={close} />
            ) : null}
            {action === 'sell' && running !== null ? <SellForm organizationId={venue.organizationId} session={running} onDone={close} /> : null}
            {action === 'comp' && running !== null ? <CompForm organizationId={venue.organizationId} sessionId={running.id} onDone={close} /> : null}
            {action === 'maintenance' ? <MaintenanceControls station={station} actions={<CloseAction onClose={close} />} onStarted={close} /> : null}
            {action === 'restart' || action === 'shutdown' ? <PowerConfirm station={station} action={action} onDone={close} /> : null}
            {action === 'skipSetup' && gates.maintenance && view.state === 'settingUp' ? <SkipSetupConfirm station={station} onDone={close} /> : null}
            {action === 'offlineCode' ? <OfflineCodeControls station={station} actions={<CloseAction onClose={close} />} /> : null}
            {action === 'recovery' ? <RecoveryCodeSection stationId={station.stationId} actions={<CloseAction onClose={close} />} /> : null}
            {action === 'screen' && canScreen ? <CountdownScreenPanel station={station} actions={<CloseAction onClose={close} />} /> : null}
          </ActionPanel>
        )}

        {items.length > 1 ? (
          <Tabs variant="pill" label={t('floor.drawer.tabs.label')} items={items} value={tab} onValueChange={pick} className="console-station-modal__tabs" />
        ) : (
          content[tab]
        )}
      </div>
      {canRescan ? (
        <StationRemovalDialog
          target={
            removal === null
              ? null
              : {
                  kind: removal,
                  organizationId: venue.organizationId,
                  venueId: venue.id,
                  stationId: station.stationId,
                  number: station.number,
                  name: station.name,
                  paired,
                }
          }
          onClose={() => setRemoval(null)}
          onRemoved={onClose}
        />
      ) : null}
      {messaging && gates.message ? (
        <Dialog
          open
          onOpenChange={(open) => (open ? undefined : setMessaging(false))}
          title={t('floor.drawer.message.title')}
          description={station.name ?? t('floor.tile.numbered', { number: station.number })}
          className="console-quick-dialog"
        >
          <MessageControls station={station} session={running} plain />
        </Dialog>
      ) : null}
    </div>
  );
}

/**
 * What is on the PC now, at the top of Overview: what it runs, and in a session the time left in the live colour and until
 * when, and what pays now. An Open console session (ADR-0034) has no end to count down to: it shows the time played.
 */
function StationNow({
  view,
  session,
  playing,
  openPlayed,
}: {
  readonly view: FloorStationView;
  readonly session: Session | null;
  readonly playing: string | null;
  readonly openPlayed: number | null;
}): ReactNode {
  const { t } = useTranslation('console');
  const overdue = view.state === 'overdue';
  const timed = LIVE_STATES.has(view.state) && view.secondsLeft !== null;
  const until = session === null ? null : timeOfDay(session.fundedEndAt);

  return (
    <div className="console-station-modal__now">
      {timed ? (
        <div className={cx('console-station-modal__clock', overdue && 'console-station-modal__clock--overdue', view.state === 'ending' && 'console-station-modal__clock--ending')}>
          <p className="console-station-modal__time">
            <span className="console-figure">{overdue ? '0:00' : clockOf(view.secondsLeft ?? 0)}</span>{' '}
            <span className="console-station-modal__unit">{t('floor.drawer.header.left')}</span>
          </p>
          {overdue || until !== null ? (
            <p className="console-station-modal__until">{overdue ? t('floor.drawer.header.grace') : t('floor.drawer.header.until', { time: until })}</p>
          ) : null}
          {session?.currentSource === null || session?.currentSource === undefined ? null : (
            <p className="console-station-modal__until">
              {t('sessions.field.payingNow', { source: t(`sessions.source.${session.currentSource}`, { defaultValue: session.currentSource }) })}
            </p>
          )}
        </div>
      ) : null}
      {openPlayed === null ? null : (
        <div className="console-station-modal__clock">
          <p className="console-station-modal__time">
            <span className="console-figure">{clockOf(openPlayed)}</span> <span className="console-station-modal__unit">{t('floor.drawer.header.played')}</span>
          </p>
          <p className="console-station-modal__until">{t('consoleSession.due.open')}</p>
        </div>
      )}
      <NowPlaying text={playing} className="console-station-sheet__playing" />
    </div>
  );
}

/**
 * The action bar: the common actions as buttons — Sell time and Add free time in a session, Message, Lock, Maintenance —
 * and the rest behind More. Lock and ending maintenance happen at once with Undo; the others open inline.
 */
function StationActionBar({
  station,
  session,
  locked,
  open,
  onToggle,
  onMessage,
  onRemoval,
  paired = false,
  can,
  canStart,
  canScreen = false,
  canWake = false,
  settingUp = false,
}: {
  readonly station: FloorStationState;
  readonly session: Session | null;
  readonly locked: boolean;
  readonly open: BarAction | null;
  readonly onToggle: (action: BarAction) => void;
  /** Message… opens its own small centred dialog. */
  readonly onMessage: () => void;
  /** Unpair… and Remove station…: given to a role with `stations:manage` only. */
  readonly onRemoval?: ((kind: RemovalTarget['kind']) => void) | undefined;
  /** A PC is paired to the station: Unpair… is offered. */
  readonly paired?: boolean;
  readonly can: StationActionGates;
  /** A free station a role may start a session on. */
  readonly canStart: boolean;
  /** A staff-run station a role may link a countdown screen to (ADR-0033). */
  readonly canScreen?: boolean;
  /** The PC is off and Wake can reach it: `stations:power`, a network address on record. */
  readonly canWake?: boolean;
  /** The PC is in its start-up setup (ADR-0055): with `stations:maintenance`, Skip setup leads the bar. */
  readonly settingUp?: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const lock = useSessionLock();
  const power = usePowerNow(station);
  const maintenance = useMaintenance(station);

  const powerItems: MenuEntry[] = can.power
    ? [
        { kind: 'label', id: 'power-label', label: t('floor.drawer.power') },
        { id: 'restart', label: t('floor.power.restart'), icon: <Icon name="refresh" />, onSelect: () => onToggle('restart') },
        { id: 'shutdown', label: t('floor.power.shutdown'), icon: <Icon name="power" />, tone: 'danger', onSelect: () => onToggle('shutdown') },
        { id: 'sleep', label: t('floor.power.sleep'), icon: <Icon name="clock" />, disabled: power.pending, onSelect: () => power.send('sleep') },
        ...(canWake ? [] : [{ id: 'wake', label: t('floor.power.wake'), icon: <Icon name="bolt" />, disabled: power.pending, onSelect: () => power.send('wake') }]),
      ]
    : [];
  const codeItems: MenuEntry[] = [
    ...(can.offlineCode ? [{ id: 'offline-code', label: t('floor.drawer.offlineCode.title'), icon: <Icon name="lock" />, onSelect: () => onToggle('offlineCode') }] : []),
    ...(can.recovery ? [{ id: 'recovery', label: t('floor.drawer.recoveryCode.title'), icon: <Icon name="shield" />, onSelect: () => onToggle('recovery') }] : []),
  ];
  const removalItems: MenuEntry[] =
    onRemoval === undefined
      ? []
      : [
          ...(paired ? [{ id: 'unpair', label: t('stations.removal.unpairAction'), onSelect: () => onRemoval('unpair') }] : []),
          { id: 'remove-station', label: t('stations.removal.removeAction'), tone: 'danger' as const, onSelect: () => onRemoval('remove') },
        ];
  const more: MenuEntry[] = [
    ...powerItems,
    ...(powerItems.length > 0 && codeItems.length > 0 ? [{ kind: 'separator' as const, id: 'codes' }] : []),
    ...codeItems,
    ...(removalItems.length > 0 && (powerItems.length > 0 || codeItems.length > 0) ? [{ kind: 'separator' as const, id: 'removal' }] : []),
    ...removalItems,
  ];

  // A booked console session (ADR-0034) is paid once at the end: its Sell time books one more block, Controllers sits
  // beside it, and there is no free time.
  const booked = session?.booking !== undefined;
  if (!canStart && !can.sell && !can.comp && !can.lock && !can.message && !can.maintenance && !canScreen && !canWake && more.length === 0) {
    return (
      <p className="console-muted console-station-modal__no-actions" role="note">
        {t('floor.drawer.noActions')}
      </p>
    );
  }

  return (
    <div className="console-station-modal__actions" role="group" aria-label={t('floor.drawer.bar.label')}>
      {settingUp && can.maintenance ? (
        <Button variant="primary" size="sm" icon={<Icon name="next" />} aria-expanded={open === 'skipSetup'} onClick={() => onToggle('skipSetup')}>
          {t('floor.drawer.skipSetup.action')}
        </Button>
      ) : null}
      {canStart ? (
        <Button variant="primary" size="sm" icon={<Icon name="plus" />} aria-expanded={open === 'start'} onClick={() => onToggle('start')}>
          {t('sessions.start.open')}
        </Button>
      ) : null}
      {can.sell && booked ? (
        <>
          <Button variant="primary" size="sm" icon={<Icon name="plus" />} aria-expanded={open === 'addTime'} onClick={() => onToggle('addTime')}>
            {t('consoleSession.addTime.title')}
          </Button>
          <Button variant="secondary" size="sm" icon={<Icon name="grid" />} aria-expanded={open === 'controllers'} onClick={() => onToggle('controllers')}>
            {t('consoleSession.controllers.title')}
          </Button>
        </>
      ) : null}
      {/* A guest (F-SES-07) plays on the guest pass alone: no time is sold onto it on account. */}
      {can.sell && !booked && session?.guest === undefined ? (
        <Button variant="primary" size="sm" icon={<Icon name="plus" />} aria-expanded={open === 'sell'} onClick={() => onToggle('sell')}>
          {t('sessions.actions.sell')}
        </Button>
      ) : null}
      {can.comp && !booked ? (
        <Button variant="secondary" size="sm" icon={<Icon name="gift" />} aria-expanded={open === 'comp'} onClick={() => onToggle('comp')}>
          {t('floor.drawer.bar.addTime')}
        </Button>
      ) : null}
      {canWake ? (
        <Button variant="primary" size="sm" icon={<Icon name="bolt" />} busy={power.pending} onClick={() => power.send('wake')}>
          {t('floor.power.wake')}
        </Button>
      ) : null}
      {can.message ? (
        <Button variant="secondary" size="sm" icon={<Icon name="message" />} aria-haspopup="dialog" onClick={onMessage}>
          {t('floor.drawer.bar.messageOpen')}
        </Button>
      ) : null}
      {can.lock && session !== null ? (
        <Button variant="secondary" size="sm" icon={<Icon name={locked ? 'unlock' : 'lock'} />} busy={lock.pending} onClick={() => lock.send(session.stationId, !locked, true, session)}>
          {session.staffRun === true ? (locked ? t('floor.drawer.bar.unlock') : t('floor.drawer.bar.lock')) : locked ? t('sessions.actions.unlock') : t('sessions.actions.lock')}
        </Button>
      ) : null}
      {can.maintenance ? (
        maintenance.inMaintenance ? (
          <Button variant="secondary" size="sm" icon={<Icon name="wrench" />} busy={maintenance.pending} onClick={() => maintenance.end(true)}>
            {t('floor.drawer.endMaintenance')}
          </Button>
        ) : (
          <Button variant="secondary" size="sm" icon={<Icon name="wrench" />} aria-expanded={open === 'maintenance'} onClick={() => onToggle('maintenance')}>
            {t('floor.drawer.maintenance')}
          </Button>
        )
      ) : null}
      {canScreen ? (
        <Button variant="secondary" size="sm" icon={<Icon name="qr" />} aria-expanded={open === 'screen'} onClick={() => onToggle('screen')}>
          {t('floor.drawer.bar.screen')}
        </Button>
      ) : null}
      {more.length > 0 ? (
        <Menu
          align="end"
          items={more}
          trigger={<Button variant="ghost" size="sm" className="console-station-modal__more" icon={<Icon name="more" />} label={t('floor.drawer.bar.more')} />}
        />
      ) : null}
    </div>
  );
}

/** Where a bar action's form opens: under the bar, scrolled into view, with focus moved into it. */
function ActionPanel({ children }: { readonly children: ReactNode }): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // After a menu has handed focus back to its trigger: move it into the form that just opened.
    const timer = window.setTimeout(() => {
      const panel = ref.current;
      if (panel === null) return;
      panel.scrollIntoView({ block: 'nearest' });
      const first = panel.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, button:not([disabled])');
      (first ?? panel).focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div ref={ref} className="console-station-modal__action" tabIndex={-1}>
      {children}
    </div>
  );
}

function CloseAction({ onClose }: { readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return <Button variant="ghost" size="sm" icon={<Icon name="close" />} label={t('floor.drawer.bar.close')} onClick={onClose} />;
}

